import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, ExternalLink, ThumbsUp, X, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon
const createCustomIcon = (color = '#10b981') => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
      ">
        <div style="
          transform: rotate(45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          color: white;
          font-size: 12px;
          font-weight: bold;
        ">
          🚀
        </div>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30],
  });
};

// City coordinates mapping
const CITY_COORDINATES = {
  // North America
  'San Francisco': [37.7749, -122.4194],
  'New York': [40.7128, -74.0060],
  'Austin': [30.2672, -97.7431],
  'Seattle': [47.6062, -122.3321],
  'Los Angeles': [34.0522, -118.2437],
  'Toronto': [43.6532, -79.3832],
  'Vancouver': [49.2827, -123.1207],
  'Montreal': [45.5017, -73.5673],
  
  // Europe
  'London': [51.5074, -0.1278],
  'Manchester': [53.4808, -2.2426],
  'Edinburgh': [55.9533, -3.1883],
  'Berlin': [52.5200, 13.4050],
  'Munich': [48.1351, 11.5820],
  'Hamburg': [53.5511, 9.9937],
  'Paris': [48.8566, 2.3522],
  'Lyon': [45.7640, 4.8357],
  'Marseille': [43.2965, 5.3698],
  'Amsterdam': [52.3676, 4.9041],
  'Rotterdam': [51.9244, 4.4777],
  'Stockholm': [59.3293, 18.0686],
  'Gothenburg': [57.7089, 11.9746],
  'Oslo': [59.9139, 10.7522],
  'Bergen': [60.3913, 5.3221],
  'Copenhagen': [55.6761, 12.5683],
  'Madrid': [40.4168, -3.7038],
  'Barcelona': [41.3851, 2.1734],
  'Milan': [45.4642, 9.1900],
  'Rome': [41.9028, 12.4964],
  'Prague': [50.0755, 14.4378],
  'Brno': [49.1951, 16.6068],
  'Istanbul': [41.0082, 28.9784],
  'Ankara': [39.9334, 32.8597],
  
  // Asia
  'Tokyo': [35.6762, 139.6503],
  'Osaka': [34.6937, 135.5023],
  'Kyoto': [35.0116, 135.7681],
  'Beijing': [39.9042, 116.4074],
  'Shanghai': [31.2304, 121.4737],
  'Shenzhen': [22.5431, 114.0579],
  'Singapore': [1.3521, 103.8198],
  'Bangkok': [13.7563, 100.5018],
  'Chiang Mai': [18.7883, 98.9853],
  'Seoul': [37.5665, 126.9780],
  'Busan': [35.1796, 129.0756],
  'Tel Aviv': [32.0853, 34.7818],
  'Jerusalem': [31.7683, 35.2137],
  'Dubai': [25.2048, 55.2708],
  'Abu Dhabi': [24.4539, 54.3773],
  'Bangalore': [12.9716, 77.5946],
  'Mumbai': [19.0760, 72.8777],
  'Delhi': [28.7041, 77.1025],
  'Manila': [14.5995, 120.9842],
  'Cebu': [10.3157, 123.8854],
  'Karachi': [24.8607, 67.0011],
  'Lahore': [31.5204, 74.3587],
  'Taipei': [25.0330, 121.5654],
  'Kaohsiung': [22.6273, 120.3014],
  
  // South America
  'São Paulo': [-23.5505, -46.6333],
  'Rio de Janeiro': [-22.9068, -43.1729],
  
  // Oceania
  'Sydney': [-33.8688, 151.2093],
  'Melbourne': [-37.8136, 144.9631],
  'Brisbane': [-27.4698, 153.0251],
};

// Get coordinates for a startup
const getStartupCoordinates = (startup) => {
  if (startup.city && CITY_COORDINATES[startup.city]) {
    // Add small random offset to prevent overlapping markers
    const offset = () => (Math.random() - 0.5) * 0.02;
    const coords = CITY_COORDINATES[startup.city];
    return [coords[0] + offset(), coords[1] + offset()];
  }
  return null;
};

// Map bounds fitter component
const FitBounds = ({ markers }) => {
  const map = useMap();
  
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => m.coords));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [markers, map]);
  
  return null;
};

const StartupMap = ({ startups }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedStartup, setSelectedStartup] = useState(null);

  // Filter startups with valid coordinates
  const markersData = startups
    .map(startup => {
      const coords = getStartupCoordinates(startup);
      return coords ? { startup, coords } : null;
    })
    .filter(Boolean);

  // Group by location for counting
  const locationCounts = markersData.reduce((acc, { startup }) => {
    const key = startup.city || startup.countryName || 'Unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const totalLocations = Object.keys(locationCounts).length;

  if (markersData.length === 0) {
    return null;
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 ${
      isExpanded ? 'fixed inset-4 z-50' : ''
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-emerald-600" />
          <h3 className="font-semibold text-gray-900">Startup Locations</h3>
          <span className="text-sm text-gray-500">
            {markersData.length} startups across {totalLocations} cities
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-500 hover:text-gray-700"
          >
            {isExpanded ? (
              <>
                <Minimize2 className="w-4 h-4 mr-1" />
                Minimize
              </>
            ) : (
              <>
                <Maximize2 className="w-4 h-4 mr-1" />
                Expand
              </>
            )}
          </Button>
          {isExpanded && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Map */}
      <div className={`relative ${isExpanded ? 'h-[calc(100%-60px)]' : 'h-[400px]'}`}>
        <MapContainer
          center={[30, 0]}
          zoom={2}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={isExpanded}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds markers={markersData} />
          
          {markersData.map(({ startup, coords }) => (
            <Marker
              key={startup.id}
              position={coords}
              icon={createCustomIcon()}
              eventHandlers={{
                click: () => setSelectedStartup(startup),
              }}
            >
              <Popup>
                <div className="min-w-[200px]">
                  <div className="flex items-start gap-3">
                    <img
                      src={startup.image}
                      alt={startup.name}
                      className="w-16 h-12 rounded object-cover bg-gray-100"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 truncate">{startup.name}</h4>
                      <p className="text-xs text-gray-500 truncate">
                        {startup.city}, {startup.countryName}
                      </p>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">{startup.description}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <ThumbsUp className="w-3 h-3" />
                      <span>{startup.rating}</span>
                    </div>
                    <a
                      href={`/startup/${startup.slug}`}
                      className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700"
                    >
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Location stats bar */}
      {!isExpanded && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 overflow-x-auto">
          <div className="flex items-center gap-3 text-sm">
            {Object.entries(locationCounts)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 8)
              .map(([location, count]) => (
                <span key={location} className="flex items-center gap-1 text-gray-600 whitespace-nowrap">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                  {location}: {count}
                </span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StartupMap;
